import React, {
  useMemo,
  useState,
  useRef,
  useLayoutEffect,
  useCallback,
  useEffect,
} from "react";
import StandardEmpty from "../empty/StandardEmpty";
import { formatCompactValue } from "../../../utils/formatting/currency";

/**
 * @component HistoryChart
 * @description SVG Bar Chart with responsive padding adjustments and externalized CSS classes.
 */
const HistoryChart = ({
  data = [],
  referenceLines = [],
  height = 200,
  title = "",
  emptyMsg = "No history yet",
  onBarClick = null,
  showXAxisLabels = true,
  showYAxis = true,
  showBarValues = false,
  colorMap = null,
  showLegend = true,
  defaultBarColor = "#2563EB",
  enableScroll = true,
}) => {
  const containerRef = useRef(null);

  const [selectedIndex, setSelectedIndex] = useState(null);
  const [hoverIndex, setHoverIndex] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  const [scrollState, setScrollState] = useState({
    canScrollLeft: false,
    canScrollRight: false,
  });

  // Listen for mobile screen width changes (768px threshold)
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const isEmpty = data.length === 0;

  const activeColorMap = useMemo(() => {
    if (colorMap) return colorMap;
    if (showLegend) {
      return {
        Save: "#a6e510",
        "At Risk": "#ff8b70",
        Passed: "#c70000",
      };
    }
    return {};
  }, [colorMap, showLegend]);

  const barWidth = 52;
  const barGap = 14;

  // Set padding.left to 0 on mobile so bars align flush with the left container edge
  const padding = useMemo(
    () => ({
      top: 30,
      right: isMobile ? 0 : 30,
      bottom: 25,
      left: isMobile ? 0 : showYAxis ? 50 : 0,
    }),
    [isMobile, showYAxis],
  );

  const contentWidth = useMemo(() => {
    if (isEmpty) return 0;
    return padding.left + padding.right + data.length * (barWidth + barGap);
  }, [data.length, isEmpty, padding.left, padding.right]);

  const updateLayout = useCallback(() => {
    const container = containerRef.current;
    if (!container || isEmpty) return;

    const { clientWidth, scrollLeft } = container;
    const maxScroll = contentWidth - clientWidth;

    if (maxScroll <= 2) {
      setScrollState({ canScrollLeft: false, canScrollRight: false });
    } else {
      setScrollState({
        canScrollLeft: scrollLeft > 2,
        canScrollRight: scrollLeft < maxScroll - 2,
      });
    }
  }, [contentWidth, isEmpty]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    if (data.length > 0) {
      setSelectedIndex(data.length - 1);
      const clientWidth = container.clientWidth;
      const maxScroll = contentWidth - clientWidth;

      if (maxScroll > 0) {
        container.scrollLeft = maxScroll;
        setScrollState({ canScrollLeft: true, canScrollRight: false });
      } else {
        container.scrollLeft = 0;
        setScrollState({ canScrollLeft: false, canScrollRight: false });
      }
    } else {
      setSelectedIndex(null);
      setScrollState({ canScrollLeft: false, canScrollRight: false });
    }
  }, [data, contentWidth]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || isEmpty) return;

    const handleScroll = () => updateLayout();
    container.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", updateLayout);

    return () => {
      container.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", updateLayout);
    };
  }, [updateLayout, isEmpty]);

  const maxY = useMemo(() => {
    if (isEmpty) return 100;
    const values = data.map((d) => d.value);
    const refs = referenceLines.map((r) => r.value);
    const highest = Math.max(...values, ...refs, 100);
    return Math.ceil(highest / 100) * 100;
  }, [data, isEmpty, referenceLines]);

  const chartHeight = height - padding.top - padding.bottom;
  const getY = (val) =>
    padding.top + (chartHeight - (val / maxY) * chartHeight);

  const handleScroll = (direction) => {
    if (!containerRef.current || isEmpty) return;
    const scrollAmount = direction === "left" ? -250 : 250;
    containerRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
  };

  return (
    <div className="scalable-history-chart-wrapper">
      {/* Header Row */}
      {(title ||
        (!isEmpty && showLegend && Object.keys(activeColorMap).length > 0) ||
        (!isEmpty && enableScroll)) && (
        <div className="history-chart-header">
          <h4 className="history-chart-title">{title}</h4>

          {!isEmpty && (
            <div className="history-chart-controls">
              {showLegend && Object.keys(activeColorMap).length > 0 && (
                <div className="history-chart-legend-group">
                  {Object.entries(activeColorMap).map(([label, color]) => (
                    <div key={label} className="history-chart-legend-item">
                      <div
                        className="history-chart-legend-dot"
                        style={{ backgroundColor: color }}
                      />
                      <span className="history-chart-legend-text">{label}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Navigation Arrows */}
              {enableScroll && (
                <div className="history-chart-nav-group">
                  <button
                    type="button"
                    disabled={!scrollState.canScrollLeft}
                    onClick={() => handleScroll("left")}
                    className="carousel-nav-btn"
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    disabled={!scrollState.canScrollRight}
                    onClick={() => handleScroll("right")}
                    className="carousel-nav-btn"
                  >
                    ›
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Main Container */}
      <div ref={containerRef} className="history-chart-scroll-container">
        {isEmpty ? (
          <div
            className="history-chart-empty-container"
            style={{ height: `${height}px` }}
          >
            <StandardEmpty msg={emptyMsg} />
          </div>
        ) : (
          <div
            className="history-chart-svg-content"
            style={{
              width: `${contentWidth}px`,
              minWidth: `${contentWidth}px`,
            }}
          >
            <svg width={contentWidth} height={height}>
              {/* Y-Axis Grid Lines & Labels */}
              {showYAxis &&
                [0, 0.5, 1].map((p) => (
                  <React.Fragment key={p}>
                    <line
                      x1={padding.left}
                      x2={
                        Math.max(
                          contentWidth,
                          containerRef.current?.clientWidth || 0,
                        ) - padding.right
                      }
                      y1={getY(maxY * p)}
                      y2={getY(maxY * p)}
                      className="history-chart-grid-line"
                    />
                    <text
                      x={padding.left - 12}
                      y={getY(maxY * p)}
                      textAnchor="end"
                      alignmentBaseline="middle"
                      className="chart-y-axis-text"
                    >
                      {formatCompactValue(Math.round(maxY * p))}
                    </text>
                  </React.Fragment>
                ))}

              {/* Reference Lines */}
              {referenceLines.map((ref, i) => (
                <g key={i}>
                  <line
                    x1={padding.left}
                    x2={
                      Math.max(
                        contentWidth,
                        containerRef.current?.clientWidth || 0,
                      ) - padding.right
                    }
                    y1={getY(ref.value)}
                    y2={getY(ref.value)}
                    stroke={ref.color || "#EF4444"}
                    className="history-chart-ref-line"
                  />
                  <text
                    x={
                      Math.max(
                        contentWidth,
                        containerRef.current?.clientWidth || 0,
                      ) -
                      padding.right +
                      5
                    }
                    y={getY(ref.value)}
                    className="history-chart-ref-text"
                    alignmentBaseline="middle"
                  >
                    {ref.label}
                  </text>
                </g>
              ))}

              {/* Bars */}
              {data.map((item, i) => {
                const x = padding.left + i * (barWidth + barGap) + barGap / 2;
                const calculatedHeight = (item.value / maxY) * chartHeight;

                const minBarHeight = item.value > 0 ? 20 : 4;
                const barHeight = Math.max(calculatedHeight, minBarHeight);
                const y = getY(item.value) - (barHeight - calculatedHeight);

                const activeIdx =
                  hoverIndex !== null ? hoverIndex : selectedIndex;
                const isSelected = activeIdx === i;

                return (
                  <g
                    key={item.uuid || i}
                    onMouseEnter={() => setHoverIndex(i)}
                    onMouseLeave={() => setHoverIndex(null)}
                    onClick={() => {
                      setSelectedIndex(i);
                      if (onBarClick) onBarClick(item);
                    }}
                    className={`history-chart-bar-group ${
                      onBarClick ? "interactive" : ""
                    }`}
                  >
                    {/* VALUE ON TOP OF BAR */}
                    {showBarValues && (
                      <text
                        x={x + barWidth / 2}
                        y={y - 6}
                        textAnchor="middle"
                        className={`chart-bar-value-text ${
                          isSelected ? "selected" : ""
                        }`}
                      >
                        {formatCompactValue(item.value)}
                      </text>
                    )}

                    <rect
                      x={x}
                      y={y}
                      width={barWidth}
                      height={barHeight}
                      fill={activeColorMap[item.status] || defaultBarColor}
                      rx="3"
                      className={`history-chart-bar-rect ${
                        isSelected ? "selected" : "dimmed"
                      }`}
                    />

                    {/* X-AXIS LABELS */}
                    {showXAxisLabels && (
                      <text
                        x={x + barWidth / 2}
                        y={height - 5}
                        textAnchor="middle"
                        className={`chart-x-axis-text ${
                          isSelected ? "selected" : ""
                        }`}
                      >
                        {item.label}
                      </text>
                    )}
                  </g>
                );
              })}
            </svg>
          </div>
        )}
      </div>
    </div>
  );
};

export default HistoryChart;
